'use client';

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';

interface SearchContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const SearchContext = createContext<SearchContextValue | undefined>(undefined);

interface SearchResult {
  url: string;
  title: string;
  excerpt: string;
}

interface SearchProviderProps {
  children: ReactNode;
}

export function SearchProvider({ children }: SearchProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Handle Cmd+K keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Search when query changes
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await fetch('/api/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query }),
        });

        if (response.ok) {
          const data = await response.json();
          setResults(data.results || []);
        }
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [query]);

  const handleSelect = useCallback((result: SearchResult) => {
    window.location.href = result.url;
    setIsOpen(false);
  }, []);

  return (
    <SearchContext.Provider value={{ isOpen, setIsOpen }}>
      {children}
      {isOpen && (
        /* Search Modal Overlay */
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-[20vh]"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="bg-background w-full max-w-2xl mx-4 rounded-lg shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Input */}
            <div className="border-b p-4">
              <div className="flex items-center gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-muted-foreground"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search documentation..."
                  className="flex-1 bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
                  autoFocus
                />
                <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs text-muted-foreground bg-muted rounded border">
                  ESC
                </kbd>
              </div>
            </div>

            {/* Search Results */}
            <div className="max-h-[60vh] overflow-y-auto">
              {isLoading && (
                <div className="p-8 text-center text-muted-foreground">
                  <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
                  Searching...
                </div>
              )}

              {!isLoading && results.length === 0 && query && (
                <div className="p-8 text-center text-muted-foreground">
                  No results found for &quot;{query}&quot;
                </div>
              )}

              {!isLoading &&
                results.map((result) => (
                  <div
                    key={result.url}
                    className="p-4 cursor-pointer hover:bg-accent border-b last:border-b-0"
                    onClick={() => handleSelect(result)}
                  >
                    <div className="font-medium text-foreground">{result.title}</div>
                    <div
                      className="text-sm text-muted-foreground mt-1 line-clamp-2"
                      dangerouslySetInnerHTML={{ __html: result.excerpt }}
                    />
                  </div>
                ))}

              {!query && !isLoading && (
                <div className="p-8 text-center text-muted-foreground">
                  Type to search documentation...
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t p-3 text-xs text-muted-foreground flex justify-between items-center">
              <span>Press ESC to close</span>
              <span>{results.length} results</span>
            </div>
          </div>
        </div>
      )}
    </SearchContext.Provider>
  );
}

export function useSearch() {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}
